/** Phase 2E-camp-2 — pick org members to assign to a campaign.
 *
 * Reuses `GET /v1/orgs/members` so we don't introduce a new endpoint
 * for "people you can assign". Org members are shown with a search
 * box; the existing assignees are pre-checked + locked (a re-add
 * round-trip is harmless, but locking gives the UI clarity).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { fetchOrgMembers } from "@/modules/dsr/api";
import type { OrgMember } from "@/modules/dsr/types";

import { addAssignees } from "./assigneesApi";

type RejectedOperator = { user_id: number; email: string };

/** Pull the named operators out of an `operator_without_schedule` 400.
 *  Anything else — a network blip, a 500 — yields none, and the caller
 *  falls back to the generic message rather than mislabelling it. */
function operatorsWithoutSchedule(error: unknown): RejectedOperator[] {
  const body = (
    error as { response?: { data?: { detail?: string; operators?: unknown } } }
  )?.response?.data;
  if (!body || body.detail !== "operator_without_schedule") return [];
  return Array.isArray(body.operators)
    ? (body.operators as RejectedOperator[])
    : [];
}


export function AddAssigneeModal({
  campaignId,
  existing,
  onClose,
}: {
  campaignId: string;
  existing: Set<number>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<number>>(new Set());

  const members = useQuery({
    queryKey: ["org-members-for-assign", campaignId],
    queryFn: () => fetchOrgMembers(),
  });

  const add = useMutation({
    mutationFn: (userIds: number[]) => addAssignees(campaignId, userIds),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["campaign-assignees", campaignId],
      });
      onClose();
    },
  });

  const filtered = useMemo(() => {
    const all: OrgMember[] = members.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((m) => {
      const name = [m.first_name, m.last_name].filter(Boolean).join(" ");
      return (
        m.email.toLowerCase().includes(q)
        || name.toLowerCase().includes(q)
      );
    });
  }, [members.data, search]);

  // The API rejects an operator with no working schedule: one silently
  // inflates the campaign's capacity maths AND is offered for booking at
  // any hour.
  const noSchedule = operatorsWithoutSchedule(add.error);

  const toggle = (userId: number) => {
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const onSubmit = () => {
    if (picked.size === 0) {
      onClose();
      return;
    }
    add.mutate([...picked]);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("ui.addAssigneeModal.dialogLabel")}
      className="modal-backdrop bg-black/70"
    >
      <div className="modal-panel flex w-full max-w-lg flex-col gap-3 max-h-[90vh]">
        <header>
          <h2 className="text-lg font-semibold">{t("ui.addAssigneeModal.title")}</h2>
          <p className="text-xs text-nb-text-3">
            {t("ui.addAssigneeModal.subtitle")}
          </p>
        </header>

        <input
          className="input"
          placeholder={t("ui.addAssigneeModal.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />

        <div className="flex-1 overflow-y-auto">
          {members.isLoading && (
            <p className="text-xs text-nb-text-3">{t("ui.addAssigneeModal.loading")}</p>
          )}
          {filtered.length === 0 && !members.isLoading && (
            <p className="text-xs text-nb-text-3">
              {t("ui.addAssigneeModal.noMatches")}
            </p>
          )}
          <ul className="divide-y divide-nb-border">
            {filtered.map((m) => {
              const name =
                [m.first_name, m.last_name].filter(Boolean).join(" ") || null;
              const alreadyAssigned = existing.has(m.user_id);
              const checked = alreadyAssigned || picked.has(m.user_id);
              return (
                <li key={m.user_id} className="py-2">
                  <label className="flex items-center justify-between gap-3">
                    <span className="flex flex-col">
                      <span className="text-sm text-nb-text">
                        {name ?? m.email}
                      </span>
                      {name && (
                        <span className="text-xs text-nb-text-3">
                          {m.email}
                        </span>
                      )}
                      {alreadyAssigned && (
                        <span className="text-xs text-nb-text-3">
                          {t("ui.addAssigneeModal.alreadyAssigned")}
                        </span>
                      )}
                    </span>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={alreadyAssigned}
                      onChange={() => toggle(m.user_id)}
                    />
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        {/* A rejection for "no schedule" is a fixable instruction, not a
            failure — name who, and say what to do. A generic "could not
            add" would leave the operator guessing at a one-minute fix. */}
        {add.isError && (
          <div className="space-y-1 text-xs text-cvp-danger">
            {noSchedule.length > 0 ? (
              <>
                <p>{t("ui.addAssigneeModal.needsSchedule")}</p>
                <ul className="list-inside list-disc text-nb-text-2">
                  {noSchedule.map((o) => (
                    <li key={o.user_id}>{o.email || `#${o.user_id}`}</li>
                  ))}
                </ul>
                <p className="text-nb-text-3">
                  {t("ui.addAssigneeModal.needsScheduleHint")}
                </p>
              </>
            ) : (
              <p>{t("ui.addAssigneeModal.error")}</p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={onClose}
            disabled={add.isPending}
          >
            {t("ui.addAssigneeModal.cancel")}
          </button>
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={onSubmit}
            disabled={add.isPending || picked.size === 0}
            data-testid="assignees-confirm-add"
          >
            {add.isPending
              ? t("ui.addAssigneeModal.adding")
              : picked.size
                ? t("ui.addAssigneeModal.addCount", { count: picked.size })
                : t("ui.addAssigneeModal.add")}
          </button>
        </div>
      </div>
    </div>
  );
}
